import { apiClient } from '../client';

export const schedulingApi = {

    async getEventTypes(userId?: string, workspaceId?: string | null) {
        const params: Record<string, string> = {};
        if (userId) params.userId = userId;
        if (workspaceId) params.workspace_id = workspaceId;
        return apiClient.get('/scheduling/event-types', { params });
    },

    async getEventTypeById(id: string) {
        return apiClient.get(`/scheduling/event-types/${id}`);
    },

    async getEventTypeBySlug(slug: string) {
        return apiClient.get(`/scheduling/event-types/slug/${slug}`);
    },

    async getEventTypeByOrgAndSlug(orgSlug: string, eventSlug: string) {
        return apiClient.get(`/scheduling/event-types/org/${orgSlug}/${eventSlug}`);
    },

    async createEventType(data: {
        name: string;
        duration: number;
        description?: string;
        color?: string;
        custom_questions?: any[];
        max_invitees?: number;
        location_type?: string;
        location_details?: string;
        event_kind?: 'slots' | 'webinar';
        sessions?: { id?: string; start_time: string; end_time?: string; capacity?: number | null; timezone?: string }[];
        price?: number | null;
        currency?: string;
        workspace_id?: string | null;
    }) {
        return apiClient.post('/scheduling/event-types', data);
    },

    async updateEventType(id: string, data: Partial<{
        name: string;
        duration: number;
        description: string;
        color: string;
        active: boolean;
        custom_questions: any[];
        max_invitees: number;
        location_type: string;
        location_details: string;
        sessions: { id?: string; start_time: string; end_time?: string; capacity?: number | null; timezone?: string }[];
        price: number | null;
        currency: string;
    }>) {
        return apiClient.put(`/scheduling/event-types/${id}`, data);
    },

    async deleteEventType(id: string, options?: { force?: boolean }) {
        if (options?.force) {
            return apiClient.delete(`/scheduling/event-types/${id}?force=true`);
        }
        return apiClient.delete(`/scheduling/event-types/${id}`);
    },

    async getBookings(eventTypeId?: string, workspaceId?: string | null) {
        const params: Record<string, string> = {};
        if (eventTypeId) params.eventTypeId = eventTypeId;
        if (workspaceId) params.workspace_id = workspaceId;
        return apiClient.get('/scheduling/bookings', { params });
    },

    async getBookingsPaged(params: {
        eventTypeId?: string;
        limit?: number;
        offset?: number;
        timeFilter?: 'upcoming' | 'past' | 'all';
        status?: string;
        search?: string;
        dateFrom?: string;
        dateTo?: string;
        workspaceId?: string | null;
    } = {}) {
        const { workspaceId, ...rest } = params;
        return apiClient.get('/scheduling/bookings', {
            params: {
                ...rest,
                workspace_id: workspaceId || undefined,
                search: params.search && params.search.trim().length > 0 ? params.search : undefined,
                status: params.status && params.status.trim().length > 0 ? params.status : undefined,
                dateFrom: params.dateFrom && params.dateFrom.trim().length > 0 ? params.dateFrom : undefined,
                dateTo: params.dateTo && params.dateTo.trim().length > 0 ? params.dateTo : undefined,
            }
        });
    },

    async getBookingById(id: string) {
        return apiClient.get(`/scheduling/bookings/${id}`);
    },

    // Invitee confirmation for /booking-success. Served by a public backend
    // route that ignores the viewer's session and workspace entirely — the
    // booking belongs to the host's workspace, not the (possibly logged-in)
    // invitee's. Display-only fields, event type nested (null if deleted).
    async getPublicBookingConfirmation(id: string) {
        return apiClient.get(`/scheduling/public/bookings/${encodeURIComponent(id)}`);
    },

    async createBooking(data: {
        event_type_id: string;
        session_id?: string;
        start_time: string;
        end_time: string;
        invitee_name: string;
        invitee_email: string;
        invitee_phone: string;
        guest_emails?: string[];
        location_type?: string;
        location_details?: string;
        notes?: string;
        timezone?: string;
        answers?: any[];
        whatsapp_reminder_enabled?: boolean;
    }) {
        return apiClient.post('/scheduling/bookings', data);
    },

    async notifyBookingFailure(data: {
        event_type_id: string;
        requested_start_time?: string;
        invitee_email: string;
        invitee_name?: string;
        invitee_phone?: string;
    }) {
        return apiClient.post('/scheduling/bookings/notify-failure', data);
    },

    async getSchedulingContacts(params: { limit?: number; offset?: number; search?: string } = {}) {
        return apiClient.get('/scheduling/contacts', {
            params: {
                ...params,
                search: params.search && params.search.trim().length > 0 ? params.search : undefined,
            }
        });
    },

    async cancelBooking(
        id: string,
        cancellation_reason?: string,
        options?: { actor?: 'invitee' },
    ) {
        return apiClient.post(`/scheduling/bookings/${id}/cancel`, {
            cancellation_reason,
            ...(options?.actor ? { actor: options.actor } : {}),
        });
    },

    async rescheduleBooking(id: string, data: {
        start_time: string;
        end_time: string;
    }) {
        return apiClient.post(`/scheduling/bookings/${id}/reschedule`, data);
    },

    async getAvailableSlots(eventTypeId: string, startDate: string, endDate: string) {
        return apiClient.get(`/scheduling/availability/${eventTypeId}`, {
            params: { startDate, endDate }
        });
    },

    async getAvailabilitySettings(userId?: string, eventTypeId?: string) {
        const params: any = {};
        if (userId) params.userId = userId;
        if (eventTypeId) params.eventTypeId = eventTypeId;
        return apiClient.get('/scheduling/availability-settings', { params });
    },

    async getDefaultAvailability(userId?: string) {
        const params = userId ? { userId } : {};
        return apiClient.get('/scheduling/availability-settings/default', { params });
    },

    async createAvailabilitySetting(data: {
        user_id?: string;
        event_type_id?: string | null;
        timezone?: string;
        slot_duration?: number;
        monday_start?: number | null;
        monday_end?: number | null;
        tuesday_start?: number | null;
        tuesday_end?: number | null;
        wednesday_start?: number | null;
        wednesday_end?: number | null;
        thursday_start?: number | null;
        thursday_end?: number | null;
        friday_start?: number | null;
        friday_end?: number | null;
        saturday_start?: number | null;
        saturday_end?: number | null;
        sunday_start?: number | null;
        sunday_end?: number | null;
        break_start?: number | null;
        break_end?: number | null;
        breaks?: { id: string; name: string; start: number; end: number }[];
    }) {
        return apiClient.post('/scheduling/availability-settings', data);
    },

    async updateAvailabilitySetting(id: string, data: any) {
        return apiClient.put(`/scheduling/availability-settings/${id}`, data);
    },

    async deleteAvailabilitySetting(id: string) {
        return apiClient.delete(`/scheduling/availability-settings/${id}`);
    },

    async getAvailableSlotsForDate(eventTypeId: string, date: string) {
        return apiClient.get(`/scheduling/availability/${eventTypeId}`, {
            params: { startDate: date, endDate: date }
        });
    },

    async getAvailableSlotsRange(eventTypeId: string, startDate: string, endDate: string) {
        return apiClient.get(`/scheduling/availability/${eventTypeId}/range`, {
            params: { startDate, endDate }
        });
    },

    async getIntegrationStatus(userId?: string) {
        const params = userId ? { userId } : {};
        return apiClient.get('/scheduling/integrations/status', { params });
    },

    async toggleIntegration(provider: 'google' | 'microsoft' | 'zoom' | 'google_sheets' | 'google_calendar', status: boolean, userId?: string) {
        return apiClient.post('/scheduling/integrations/toggle', { provider, status, userId });
    },

    async getPaymentGatewayStatus() {
        return apiClient.get('/scheduling/payment-gateway-status');
    },

    async getSchedulingUsers() {
        return apiClient.get('/scheduling/users');
    },

    async getCurrentUser() {
        return apiClient.get('/scheduling/me');
    },
};
